import "./Field.scss";

import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import clsx from "clsx";
import TextField from "@mui/material/TextField";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
	DateTimePicker,
	LocalizationProvider,
	DatePicker,
} from "@mui/x-date-pickers";
import { FaEye, FaEyeSlash } from "react-icons/fa";

import * as StringUtils from "../../../lib/util/StringUtils";
import * as FormUtils from "../../../lib/util/FormUtils";
import * as LocaleUtils from "../../../lib/util/LocaleUtils";
import * as Utils from "../../../lib/util/Utils";

import Select from "../Select/Select";
import RichText from "../../common/RichText/RichText";
import RadioButton from "../RadioButton/RadioButton";
import IconButton from "../../common/IconButton/IconButton";
import CountrySelect from "../CountrySelect/CountrySelect";
import ColourPicker from "../ColourPicker/ColourPicker";
import Text from "../../common/Text/Text";
import FilterInput from "../FilterInput/FilterInput";
import Checkbox from "../Checkbox/Checkbox";
import TimeRangeSelector from "../TimeRangeSelector/TimeRangeSelector";
import PhoneInput from "../PhoneInput/PhoneInput";
import { LocaleContext, TextElement } from "../../../lib/util/LocaleUtils";
import { useTranslated } from "lib/hook/LocaleHooks";
import DragAndDropFile from "../DragAndDropFile/DragAndDropFile";
import ImageSelector from "../ImageSelector/ImageSelector";
import Endpoint from "../../../lib/api/Endpoint";
import Toggle from "../Toggle/Toggle";
import GroupSelectList from "../GroupSelectList/GroupSelectList";
import InfoTooltip from "../../../components/common/InfoTooltip/InfoTooltip";

export interface FieldProps {
	tabIndex?: string;
	style?: React.CSSProperties;
	rows?: any;
	autoComplete?: boolean | "on";
	type?: string;
	headerRightItem?: React.ReactNode;
	className?: string;
	showErrors?: boolean;
	noFieldClass?: boolean;
	value?: any;
	errorMessage?: any;
	defaultValue?: any;
	id?: string;
	min?: number;
	loading?: boolean;
	max?: number;
	autoFocus?: boolean;
	options?: any;
	required?: boolean;
	hide?: boolean;
	rounded?: boolean;
	readOnly?: boolean;
	validators?: any[];
	small?: boolean;
	inline?: boolean;
	widget?: boolean;
	compact?: boolean;
	children?: React.ReactNode;
	hidden?: boolean;
	meta?: any;
	defaultMeta?: any;
	name?: TextElement;
	disabled?: boolean | string;
	onChange?: any;
	onValueChange?: any;
	noClear?: boolean;
	autoResize?: boolean;
	onKeyDown?: any;
	onAutoFill?: any;
	noPaste?: boolean;
	onBlur?: any;
	filterInputOptions?: any;
	placeholder?: TextElement;
	richTextToolbar?: any;
	width?: number;
	newline?: boolean;
	dateFormat?: string;
	fileTypes?: string[];
	onFileChange?: (file: File) => void;
	disableMaxLength?: boolean;
	imageSelectorProps?: {
		defaultImages: { [key: string]: string[] };
		customImageAPI: Endpoint;
		imageUploadAPI: Endpoint;
		imagePath: string;
	};
	fieldDescription?: string;
	tooltip?: {
		title?: string;
		copy?: string;
	};
	views?: string[];
}

export const MAX_INPUT_LENGTH = 100;

const Field: React.FC<FieldProps> = (props) => {
	const formContext = useContext<any>(FormUtils.FormContext);
	const localeContext = useContext(LocaleContext);
	const ref = useRef<HTMLDivElement>(null);
	const internalId = StringUtils.uuid(12);

	const [pristine, setPristine] = useState(true);
	const [reveal, setReveal] = useState(false);
	const [autoFilled, setAutoFilled] = useState(false);
	const [selfErrorMessage, setSelfErrorMessage] = useState(null);
	const [meta, setMeta] = useState({});

	let {
		onBlur,
		onAutoFill,
		noPaste,
		onKeyDown,
		rows,
		autoComplete,
		type,
		headerRightItem,
		className,
		showErrors,
		value,
		errorMessage,
		defaultValue,
		id,
		min,
		loading,
		max,
		autoFocus,
		options,
		required,
		hide,
		noFieldClass,
		rounded,
		readOnly,
		validators,
		small,
		inline,
		widget,
		compact,
		children,
		name,
		disabled: propDisabled,
		hidden,
		meta: propsMeta,
		defaultMeta,
		filterInputOptions,
		richTextToolbar,
		noClear,
		width,
		newline,
		fileTypes,
		onFileChange,
		disableMaxLength,
		imageSelectorProps,
		fieldDescription,
		tooltip,
		views,
		..._otherProps
	} = props;

	const otherProps = _otherProps as any;

	const [translatedName] = useTranslated(name);

	const toggleReveal = useCallback(() => {
		setReveal(!reveal);
	}, [reveal]);

	const disabled = useMemo(() => {
		return propDisabled || (formContext && formContext.fieldsDisabled);
	}, [propDisabled, formContext]);

	const selfValidateImmediate = useCallback(() => {
		if (validators) {
			let resolvedMeta = meta;
			if (resolvedMeta === undefined) {
				resolvedMeta = meta;
			}

			const result = FormUtils.validateValue(
				value === undefined ? defaultValue : value,
				resolvedMeta,
				validators,
				{},
				defaultMeta,
			);
			setSelfErrorMessage(!result.valid ? result.message : null);
		}
	}, [value, defaultValue, validators, defaultMeta, meta]);

	const selfValidate = Utils.debounce(selfValidateImmediate, 1000);
	const { onChange, onValueChange } = props;

	const handleChange = useCallback(
		(value: any, updatedMeta?: any) => {
			if (updatedMeta !== undefined) {
				setMeta({ ...meta, ...updatedMeta });
			}

			if (pristine) {
				setPristine(false);
			}

			if (selfErrorMessage) {
				setSelfErrorMessage(null);
			}

			const target = value && (value.currentTarget || value.target);

			if (target) {
				// eslint-disable-next-line no-param-reassign
				value = target.value;

				if (target.type === "number") {
					// eslint-disable-next-line no-param-reassign
					value = parseFloat(value);
				}
			}

			if (typeof value?.valueOf === "function") {
				// eslint-disable-next-line no-param-reassign
				value = value.valueOf();
			}

			selfValidate();

			// ID as passed through, without . replaced with _
			const propsId = props.id;

			const data = { id: propsId, value, meta, defaultValue };
			if (formContext && formContext.handleFieldChange) {
				formContext.handleFieldChange(data);
			}
			if (onChange) {
				onChange(data);
			}
			if (type === "dragAndDropFile" && onFileChange) {
				onFileChange(data.value as File);
			}
			if (onValueChange) {
				onValueChange(value);
			}
		},
		[
			formContext,
			meta,
			onChange,
			onValueChange,
			pristine,
			selfErrorMessage,
			selfValidate,
			props.id,
			onFileChange,
			type,
			defaultValue,
		],
	);

	const handleToggleChange = useCallback(
		(value) => {
			handleChange(value.index);
		},
		[handleChange],
	);

	const handleCheckboxChange = useCallback(
		(e: Event, value: any) => {
			handleChange(value);
		},
		[handleChange],
	);

	const handleFilterBoxChange = useCallback(
		(value: any, expressionsResult: any) => {
			handleChange(value, expressionsResult);
		},
		[handleChange],
	);

	const handleGroupSelectorChange = useCallback(
		(ids?: string[]) => {
			handleChange(ids);
		},
		[handleChange],
	);

	const autoResize = useCallback(() => {
		const input = ref.current?.querySelector("textarea");

		if (!input) {
			return;
		}

		input.style.height = "auto";
		input.style.height = `${input?.scrollHeight}px`;
	}, [ref]);

	const handleTextareaChange = useCallback(
		(value: any, meta: any) => {
			handleChange(value, meta);

			if (autoResize) {
				autoResize();
			}
		},
		[autoResize, handleChange],
	);

	const isFocused = useCallback(() => {
		return ref.current === document.activeElement;
	}, [ref]);

	const focus = useCallback(() => {
		if (!isFocused()) {
			const input = ref.current;
			input?.focus();
		}
	}, [ref, isFocused]);

	const checkAutofill = useCallback(
		(e: AnimationEvent) => {
			if (e.animationName === "--autofill") {
				setAutoFilled(true);
				if (formContext && formContext.handleAutoFill) {
					formContext.handleAutoFill();
				}
				if (onAutoFill) {
					onAutoFill();
				}
			}
		},
		[formContext, onAutoFill],
	);

	const handleKeyDown = useCallback(
		(e: Event) => {
			if (typeof onKeyDown === "function") {
				onKeyDown(e, id);
			}
		},
		[id, onKeyDown],
	);

	const handlePaste = useCallback(
		(e: Event) => {
			if (noPaste) {
				e.preventDefault();
			}
		},
		[noPaste],
	);

	const getFormProps = useCallback(() => {
		return (formContext && formContext.props) || {};
	}, [formContext]);

	const handleSelectChange = useCallback(
		(data: any) => {
			if (Array.isArray(data)) {
				return handleChange(data.map((option) => option.value));
			}
			return handleChange(data && data.value);
		},
		[handleChange],
	);

	const handleTimeRangeSelectChange = useCallback(
		(timeRange: any) => {
			return handleChange(timeRange.id);
		},
		[handleChange],
	);

	const handleFileDrop = useCallback(
		(files: FileList) => {
			return handleChange(files[0], files[0].name);
		},
		[handleChange],
	);

	const handleBlur = useCallback(
		(e: Event) => {
			if (formContext && formContext.handleFieldBlur) {
				formContext.handleFieldBlur(id);
			}

			selfValidateImmediate();

			if (onBlur) {
				onBlur(id);
			}
		},
		[formContext, selfValidateImmediate, onBlur, id],
	);

	useEffect(() => {
		if (reveal) {
			focus();
		}
	}, [reveal, focus]);

	useEffect(() => {
		const newMeta = { ...meta, name: translatedName };

		if (JSON.stringify(meta) !== JSON.stringify(newMeta)) {
			setMeta(newMeta);
		}
		const data = { id: id, value: value, newMeta };
		formContext?.handleFieldChange(data);

		if (onChange) {
			onChange(data);
		}
		// eslint-disable-next-line
	}, [translatedName]);

	const placeholder =
		props.placeholder &&
		LocaleUtils.translate(props.placeholder, localeContext.locale);

	autoComplete = autoComplete || "on";

	if (!id) {
		id = internalId;
	}

	id = id.replace(/\./g, "_");

	const formProps: any = getFormProps();
	["small", "widget", "compact"].forEach((key) => {
		const value = formProps[key];
		if (value !== undefined) {
			otherProps[key] = value;
		}
	});

	if (errorMessage === undefined && validators && !pristine) {
		errorMessage = selfErrorMessage;
	}

	const classes = clsx({
		"--autoFilled": autoFilled,
		"--error": errorMessage,
		"--readOnly": readOnly,
		"--disabled": disabled,
		"--rounded": rounded,
		"--compact": compact,
		"--small": small,
		"--widget": widget,
		"--inline": inline,
	});

	const inputProps: any = {
		id: id,
		meta: propsMeta,
		onKeyDown: handleKeyDown,
		defaultValue: defaultValue,
		value: value,
		readOnly: readOnly || props.readOnly,
		placeholder: placeholder,
		name: StringUtils.toCamelCase(translatedName || id),
		required: required,
		min: min,
		autoComplete: autoComplete,
		max: max,
		loading: loading,
		autoFocus: autoFocus,
		disabled: disabled,
		onPaste: handlePaste,
		className: classes,
		onAnimationStart: checkAutofill,
		small: small,
		compact: compact,
		widget: widget,
		dateFormat: props.dateFormat || "DD/MM/YYYY HH:mm",
		views: views,
	};

	const currentValue =
		(inputProps.value !== undefined
			? inputProps.value
			: inputProps.defaultValue) ?? "";

	useEffect(() => {
		if (currentValue && currentValue !== inputProps.value) {
			handleChange(currentValue);
		}
		// eslint-disable-next-line
	}, [currentValue, inputProps.value]);

	if (hidden) {
		return null;
	}

	const inputClassName = clsx(inputProps.classes, "Field__input");

	const spellcheck = type !== "email" && type !== "password";

	let renderedInput = null;

	if (type === "select" || type === "tags") {
		renderedInput = (
			<Select
				tags={type === "tags"}
				options={
					options
						? options.map((value: any) =>
								typeof value === "string" ? { value } : value,
						  )
						: []
				}
				onBlur={handleBlur}
				onChange={handleSelectChange}
				noClear={noClear}
				defaultValue={inputProps.defaultValue}
				{...inputProps}
			/>
		);
	} else if (type === "tel") {
		renderedInput = (
			<PhoneInput
				onChange={handleChange}
				onBlur={handleBlur}
				defaultMeta={defaultMeta}
				{...inputProps}
			/>
		);
	} else if (type === "country") {
		renderedInput = (
			<CountrySelect
				onBlur={handleBlur}
				onChange={handleSelectChange}
				{...inputProps}
			/>
		);
	} else if (type === "textarea") {
		renderedInput = (
			<textarea
				ref={ref}
				rows={rows}
				{...inputProps}
				className={inputClassName}
				onBlur={handleBlur}
				onChange={handleTextareaChange}
				value={inputProps.value || inputProps.defaultValue || ""}
			/>
		);
	} else if (type === "richtext") {
		renderedInput = (
			<RichText
				handleChange={handleChange}
				mentions={otherProps.mentions}
				defaultValue={{
					async: otherProps.async,
					loaded: otherProps.loaded,
					value: inputProps.defaultValue || null,
				}}
				locale={otherProps.locale}
				toolbar={richTextToolbar}
			/>
		);
	} else if (type === "radio") {
		renderedInput = (
			<RadioButton {...inputProps} onChange={handleChange} options={options} />
		);
	} else if (type === "checkbox") {
		renderedInput = (
			<Checkbox
				{...inputProps}
				checked={Boolean(
					inputProps.value !== undefined
						? inputProps.value
						: inputProps.defaultValue,
				)}
				defaultValue={inputProps.defaultValue}
				size={small ? "small" : "medium"}
				onChange={handleCheckboxChange}
			/>
		);
	} else if (type === "date") {
		renderedInput = (
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<DatePicker
					{...inputProps}
					inputFormat={props.dateFormat || "DD/MM/YYYY"}
					// eslint-disable-next-line react/jsx-no-bind
					renderInput={(params) => <TextField {...params} />}
					value={inputProps.value || inputProps.defaultValue || null}
					onChange={handleChange}
				/>
			</LocalizationProvider>
		);
	} else if (type === "datetime") {
		renderedInput = (
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<DateTimePicker
					{...inputProps}
					inputFormat={props.dateFormat || "DD/MM/YYYY HH:mm"}
					// eslint-disable-next-line react/jsx-no-bind
					renderInput={(params) => <TextField {...params} />}
					value={inputProps.value || inputProps.defaultValue || null}
					onChange={handleChange}
				/>
			</LocalizationProvider>
		);
	} else if (type === "colour") {
		if (!inputProps.value && inputProps.defaultValue) {
			handleChange(inputProps.defaultValue);
		}

		renderedInput = <ColourPicker {...inputProps} onChange={handleChange} />;
	} else if (type === "filter-box") {
		renderedInput = (
			<FilterInput
				options={filterInputOptions}
				onChange={handleFilterBoxChange}
				{...inputProps}
				className={inputClassName}
			/>
		);
	} else if (type === "timeRange") {
		renderedInput = (
			<TimeRangeSelector
				{...inputProps}
				options={options}
				onChange={handleTimeRangeSelectChange}
			/>
		);
	} else if (type === "dragAndDropFile") {
		renderedInput = (
			<DragAndDropFile
				{...inputProps}
				accept={fileTypes || []}
				onAfterDrop={handleFileDrop}
			/>
		);
	} else if (type === "imageSelector") {
		renderedInput = (
			<ImageSelector
				{...inputProps}
				{...imageSelectorProps}
				name={name}
				onChange={handleChange}
			/>
		);
	} else if (type === "toggle") {
		renderedInput = (
			<Toggle
				default={value ?? defaultValue ?? 0}
				{...inputProps}
				options={options}
				afterChange={handleToggleChange}
			/>
		);
	} else if (type === "groupSelector") {
		renderedInput = (
			<GroupSelectList
				onChange={handleGroupSelectorChange}
				defaultValue={defaultValue}
			/>
		);
	} else {
		if (type === "time") {
			if (inputProps.value && typeof inputProps.value === "object") {
				inputProps.value = `${inputProps.value.hour
					.toString()
					.padStart(2, "0")}:${inputProps.value.minute
					.toString()
					.padStart(2, "0")}`;
			}
			if (
				inputProps.defaultValue &&
				typeof inputProps.defaultValue === "object"
			) {
				inputProps.defaultValue = `${inputProps.defaultValue.hour
					.toString()
					.padStart(2, "0")}:${inputProps.defaultValue.minute
					.toString()
					.padStart(2, "0")}`;
			}
		}

		const currentValue =
			(inputProps.value !== undefined
				? inputProps.value
				: inputProps.defaultValue) ?? "";

		// Detach attributes that are not recognised by the input element
		const { small, compact, dateFormat, ...inputPropsWithoutSmall } =
			inputProps;
		renderedInput = (
			<input
				ref={ref}
				maxLength={disableMaxLength ? undefined : MAX_INPUT_LENGTH}
				spellCheck={spellcheck}
				type={reveal ? "text" : type}
				onBlur={handleBlur}
				onChange={handleChange}
				{...inputPropsWithoutSmall}
				className={inputClassName}
				defaultValue={undefined}
				value={currentValue}
			/>
		);
	}

	return (
		<>
			{newline && <div style={{ width: "100%", height: 0 }} />}
			<div
				ref={ref}
				className={clsx(
					noFieldClass ? "" : "Field",
					`-${type}`,
					`-${id}`,
					{
						"--display-none": hide,
						"--rounded": rounded,
						"--autoFilled": autoFilled,
						"--custom-width": Boolean(width),
					},
					classes,
					className,
				)}
				style={{
					flexBasis: width ? `calc(${width * 100}% - 4px)` : "auto",
				}}
			>
				{translatedName && (
					<div className={"Field__header"}>
						<label
							htmlFor={id}
							className={clsx("Field__label", {
								"-checked":
									type === "checkbox" &&
									(inputProps.value !== undefined
										? inputProps.value
										: inputProps.defaultValue),
								"-required": required,
							})}
						>
							{translatedName}
							{required && (
								<span className={"Field__required-highlight"}>*</span>
							)}
						</label>
						{headerRightItem}
						{fieldDescription && (
							<Text tag={"p"} className={"Field__description"}>
								{fieldDescription}
							</Text>
						)}
					</div>
				)}
				<div className={"Field__input-container"}>
					{renderedInput}
					{type === "password" && (
						<div className={"Field__reveal"}>
							<IconButton tabIndex={-1} onClick={toggleReveal}>
								{reveal ? <FaEye /> : <FaEyeSlash />}
							</IconButton>
						</div>
					)}
					{tooltip && (
						<InfoTooltip
							placement={"right"}
							referenceClassName={"InfoTooltip Field__input-tooltip"}
							content={
								<div className={"Field__input-tooltip-content"}>
									<h2>{tooltip.title}</h2>
									<p>{tooltip.copy}</p>
								</div>
							}
						/>
					)}
					{children}
				</div>
				{showErrors !== false && (
					<Text
						tag={"p"}
						className={`Field__error ${errorMessage ? "--visible" : ""}`}
					>
						{errorMessage}
					</Text>
				)}
			</div>
		</>
	);
};

export default Field;
